import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCard } from '@angular/material/card';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import type { JobFlowResponse, JobFlowStack, JobFlowWorkStep } from '@data-terminal/shared-models/job-flow';
import { Machine } from '@data-terminal/shared-models/machine';
import { ActivityState } from '@data-terminal/shared-models/operation';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { JobFlowNodeComponent } from '../job-flow-node/job-flow-node.component';
import { JobFlowStackCardComponent } from '../job-flow-stack-card/job-flow-stack-card.component';

interface DiagramPoint {
    x: number;
    y: number;
}

interface PositionedWorkStep extends JobFlowWorkStep {
    id: string;
    kind: 'operation';
    x: number;
    y: number;
    width: number;
    height: number;
    outputGap: number;
}

interface PositionedStackGroup {
    id: string;
    kind: 'stack-group';
    stacks: JobFlowStack[];
    sourceId: string;
    isInputStackCompleted: boolean;
    label?: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

type PositionedDiagramNode = PositionedWorkStep | PositionedStackGroup;

interface PositionedEdge {
    id: string;
    points: DiagramPoint[];
}

interface DiagramLayout {
    nodes: PositionedWorkStep[];
    stackGroups: PositionedStackGroup[];
    edges: PositionedEdge[];
    width: number;
    height: number;
}

interface DiagramConnection {
    id: string;
    sourceId: string;
    targetId: string;
}

interface RoutedConnection {
    connection: string;
    sourceId: string;
    targetId: string;
    source: PositionedDiagramNode;
    target: PositionedDiagramNode;
}

interface DeviceClassColumn {
    connected: JobFlowWorkStep[];
    disconnected: JobFlowWorkStep[];
}

interface RoutingPort {
    boundary: DiagramPoint;
    escape: DiagramPoint;
}

interface RoutingEndpoint {
    connection: string;
    role: 'source' | 'target';
    nodeId: string;
    node: PositionedDiagramNode;
    otherNode: PositionedDiagramNode;
}

interface RoutingObstacle {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

interface RouteSegment {
    start: DiagramPoint;
    end: DiagramPoint;
}

type RouteDirection = 'horizontal' | 'vertical' | 'start';

interface RouteQueueItem {
    key: string;
    xIndex: number;
    yIndex: number;
    direction: RouteDirection;
    cost: number;
    estimatedCost: number;
}

const NODE_WIDTH = 300;
const NODE_HEIGHT = 192;
const STACK_GROUP_WIDTH = 180;
const STACK_NODE_HEIGHT = 70;
const STACK_NODE_GAP = 12;
const STACK_GROUP_PADDING = 8;
const STACK_GROUP_LABEL_HEIGHT = 20;
const DEFAULT_COLUMN_GAP = 80;
const STACK_COLUMN_GAP = 320;
const ROW_GAP = 32;
const SCENE_PADDING = 12;
const EDGE_CLEARANCE = 12;
const EDGE_PORT_LENGTH = 24;
const EDGE_BEND_COST = 24;
const EDGE_CROSSING_COST = 1_000_000;
const EDGE_OVERLAP_COST = 10_000_000;

@Component({
    selector: 'data-terminal-job-flow-diagram',
    imports: [JobFlowNodeComponent, JobFlowStackCardComponent, MatCard],
    templateUrl: './job-flow-diagram.component.html',
    styleUrl: './job-flow-diagram.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFlowDiagramComponent {
    readonly #translateService = inject(TranslateService);
    readonly #userMachinesService = inject(UserMachinesService);

    public readonly flow = input.required<JobFlowResponse>();
    public readonly selectedWorkStepId = input<string>();

    readonly #machines = toSignal(
        this.#userMachinesService.getUserMachines$().pipe(map((userMachines) => userMachines.allMachines)),
        { initialValue: [] as Machine[] }
    );

    public readonly machineNames = computed(() => {
        return new Map(this.#machines().map((machine) => [machine.machineId, machine.machineName]));
    });

    readonly #diagram = computed(() => this.#createLayout(this.flow()));
    public readonly nodes = computed(() => this.#diagram().nodes);
    public readonly stackGroups = computed(() => this.#diagram().stackGroups);
    public readonly edges = computed(() => this.#diagram().edges);
    public readonly width = computed(() => this.#diagram().width);
    public readonly height = computed(() => this.#diagram().height);

    public path(points: DiagramPoint[]): string {
        return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
    }

    #createLayout(flow: JobFlowResponse): DiagramLayout {
        const { workSteps } = flow;
        const connections = this.#connections(workSteps);
        const finalStackSourceIds = flow.totalProduct.finalStacks.length
            ? this.#terminalStepIds(workSteps, connections)
            : new Set<string>();
        const columns = this.#groupByDeviceClass(workSteps, connections);
        const rowCount = Math.max(0, ...columns.map((column) => column.length));
        const rowHeights = Array.from({ length: rowCount }, (_, rowIndex) =>
            Math.max(
                ...columns.flatMap((column) =>
                    column[rowIndex]
                        ? [this.#rowHeight(column[rowIndex], finalStackSourceIds.has(column[rowIndex].opId))]
                        : []
                )
            )
        );
        const rowOffsets = rowHeights.map(
            (_, rowIndex) =>
                SCENE_PADDING +
                rowHeights.slice(0, rowIndex).reduce((height, rowHeight) => height + rowHeight + ROW_GAP, 0)
        );
        const columnGaps = columns.map((column) =>
            column.some((step) => step?.stacks.length && !finalStackSourceIds.has(step.opId))
                ? STACK_COLUMN_GAP
                : DEFAULT_COLUMN_GAP
        );
        const columnOffsets: number[] = [];
        columns.forEach((_, columnIndex) =>
            columnOffsets.push(
                SCENE_PADDING +
                    columnGaps.slice(0, columnIndex).reduce((offset, columnGap) => offset + NODE_WIDTH + columnGap, 0)
            )
        );
        const nodes = columns.flatMap((column, columnIndex) =>
            column.flatMap((step, rowIndex) =>
                step
                    ? [
                          {
                              ...step,
                              id: step.opId,
                              kind: 'operation' as const,
                              x: columnOffsets[columnIndex],
                              y: rowOffsets[rowIndex] + (rowHeights[rowIndex] - NODE_HEIGHT) / 2,
                              width: NODE_WIDTH,
                              height: NODE_HEIGHT,
                              outputGap: columnGaps[columnIndex],
                          },
                      ]
                    : []
            )
        );
        const stackGroups = this.#createStackGroups(nodes, finalStackSourceIds);
        const finalStackGroup = this.#createFinalStackGroup(nodes, flow.totalProduct.finalStacks, finalStackSourceIds);
        if (finalStackGroup) {
            stackGroups.push(finalStackGroup);
        }
        const diagramConnections = this.#diagramConnections(
            connections,
            stackGroups,
            finalStackGroup,
            finalStackSourceIds
        );
        const diagramNodes: PositionedDiagramNode[] = [...nodes, ...stackGroups];

        return {
            nodes,
            stackGroups,
            edges: this.#createEdges(diagramConnections, diagramNodes),
            width: diagramNodes.length
                ? Math.max(...diagramNodes.map((node) => node.x + node.width)) + SCENE_PADDING
                : 0,
            height: diagramNodes.length
                ? Math.max(...diagramNodes.map((node) => node.y + node.height)) + SCENE_PADDING
                : 0,
        };
    }

    #createStackGroups(nodes: PositionedWorkStep[], excludedSourceIds: Set<string>): PositionedStackGroup[] {
        return nodes.flatMap((node) => {
            if (!node.stacks.length || excludedSourceIds.has(node.opId)) {
                return [];
            }

            const height = this.#stackGroupHeight(node.stacks);
            return [
                {
                    id: this.#stackGroupId(node.opId),
                    kind: 'stack-group' as const,
                    stacks: node.stacks,
                    sourceId: node.opId,
                    isInputStackCompleted: (() => {
                        const successorIds = new Set(node.successors?.map(({ workstepId }) => workstepId) ?? []);
                        const consumingWorksteps = successorIds.size
                            ? nodes.filter((target) => successorIds.has(target.opId))
                            : nodes.filter((target) =>
                                  target.predecessors?.some(({ workstepId }) => workstepId === node.opId)
                              );
                        return (
                            consumingWorksteps.length > 0 &&
                            consumingWorksteps.every((target) => target.opState === ActivityState.COMPLETED)
                        );
                    })(),
                    x: node.x + NODE_WIDTH + (node.outputGap - STACK_GROUP_WIDTH) / 2,
                    y: node.y + NODE_HEIGHT / 2 - height / 2,
                    width: STACK_GROUP_WIDTH,
                    height,
                },
            ];
        });
    }

    #createFinalStackGroup(
        nodes: PositionedWorkStep[],
        finalStacks: JobFlowStack[],
        sourceIds: Set<string>
    ): PositionedStackGroup | undefined {
        if (!finalStacks.length || !sourceIds.size || !nodes.length) {
            return undefined;
        }

        const sourceNodes = nodes.filter((node) => sourceIds.has(node.opId));
        const sourceCenter =
            sourceNodes.reduce((total, node) => total + node.y + node.height / 2, 0) / sourceNodes.length;
        const height = this.#stackGroupHeight(finalStacks, true);

        return {
            id: 'final-stack-group',
            kind: 'stack-group',
            stacks: finalStacks,
            sourceId: sourceNodes[0].opId,
            isInputStackCompleted: false,
            label: this.#translateService.instant('DC.STACK_INFORMATION.FINAL_STACKS'),
            x: Math.max(...nodes.map((node) => node.x + node.width)) + DEFAULT_COLUMN_GAP,
            y: Math.max(SCENE_PADDING, sourceCenter - height / 2),
            width: STACK_GROUP_WIDTH,
            height,
        };
    }

    #diagramConnections(
        operationConnections: Set<string>,
        stackGroups: PositionedStackGroup[],
        finalStackGroup: PositionedStackGroup | undefined,
        finalStackSourceIds: Set<string>
    ): DiagramConnection[] {
        const stackGroupBySource = new Map(
            stackGroups.filter((group) => group !== finalStackGroup).map((group) => [group.sourceId, group])
        );
        const connections: DiagramConnection[] = [];

        for (const [sourceId, stackGroup] of stackGroupBySource) {
            connections.push({
                id: `${sourceId}\u0000${stackGroup.id}`,
                sourceId,
                targetId: stackGroup.id,
            });
        }

        for (const connection of operationConnections) {
            const [sourceId, targetId] = connection.split('\u0000');
            const stackGroup = stackGroupBySource.get(sourceId);

            if (stackGroup) {
                connections.push({
                    id: `${stackGroup.id}\u0000${targetId}`,
                    sourceId: stackGroup.id,
                    targetId,
                });
            } else {
                connections.push({ id: connection, sourceId, targetId });
            }
        }

        if (finalStackGroup) {
            for (const sourceId of finalStackSourceIds) {
                connections.push({
                    id: `${sourceId}\u0000${finalStackGroup.id}`,
                    sourceId,
                    targetId: finalStackGroup.id,
                });
            }
        }

        return connections;
    }

    #terminalStepIds(workSteps: JobFlowWorkStep[], connections: Set<string>): Set<string> {
        const sourceIds = new Set<string>();
        const targetIds = new Set<string>();

        for (const connection of connections) {
            const [sourceId, targetId] = connection.split('\u0000');
            sourceIds.add(sourceId);
            targetIds.add(targetId);
        }

        const terminalIds = new Set([...targetIds].filter((targetId) => !sourceIds.has(targetId)));
        if (terminalIds.size || !workSteps.length) {
            return terminalIds;
        }

        const sortedSteps = [...workSteps];
        sortedSteps.sort((left, right) => this.#sortOrder(right) - this.#sortOrder(left));
        const lastStep = sortedSteps[0];
        return new Set([lastStep.opId]);
    }

    #stackGroupId(operationId: string): string {
        return `stack-group:${operationId}`;
    }

    #groupByDeviceClass(workSteps: JobFlowWorkStep[], connections: Set<string>): (JobFlowWorkStep | undefined)[][] {
        const connectedStepIds = new Set([...connections].flatMap((connection) => connection.split('\u0000')));
        const columns = new Map<string, DeviceClassColumn>();
        const orderedSteps = workSteps
            .map((step, index) => ({ step, index }))
            .sort(
                (left, right) => this.#sortOrder(left.step) - this.#sortOrder(right.step) || left.index - right.index
            );

        for (const { step } of orderedSteps) {
            const deviceClass = step.deviceClass?.toUpperCase() ?? '';
            const column = columns.get(deviceClass) ?? { connected: [], disconnected: [] };
            column[connectedStepIds.has(step.opId) ? 'connected' : 'disconnected'].push(step);
            columns.set(deviceClass, column);
        }

        const orderedColumns = [...columns.values()];
        this.#minimizeCrossings(orderedColumns, connections);
        const connectedRows = this.#alignConnectedRows(orderedColumns, connections);
        const connectedRowCount = Math.max(0, ...connectedRows.map((rows) => rows.length));

        return orderedColumns.map((column, columnIndex) => [
            ...connectedRows[columnIndex],
            ...Array.from<JobFlowWorkStep | undefined>({
                length: connectedRowCount - connectedRows[columnIndex].length,
            }),
            ...column.disconnected,
        ]);
    }

    #alignConnectedRows(columns: DeviceClassColumn[], connections: Set<string>): (JobFlowWorkStep | undefined)[][] {
        const adjacentStepIds = new Map<string, string[]>();
        for (const connection of connections) {
            const [sourceId, targetId] = connection.split('\u0000');
            adjacentStepIds.set(sourceId, [...(adjacentStepIds.get(sourceId) ?? []), targetId]);
            adjacentStepIds.set(targetId, [...(adjacentStepIds.get(targetId) ?? []), sourceId]);
        }

        const rowByStepId = new Map<string, number>();
        return columns.map(({ connected }) => {
            const rows: (JobFlowWorkStep | undefined)[] = [];
            let minimumRow = 0;

            for (const step of connected) {
                const neighborRows = (adjacentStepIds.get(step.opId) ?? []).flatMap((neighborId) => {
                    const r = rowByStepId.get(neighborId);
                    return r === undefined ? [] : [r];
                });
                const preferredRow = neighborRows.length
                    ? Math.floor(neighborRows.reduce((total, r) => total + r, 0) / neighborRows.length)
                    : minimumRow;
                let row = Math.max(minimumRow, preferredRow);
                while (rows[row]) {
                    row++;
                }

                rows[row] = step;
                rowByStepId.set(step.opId, row);
                minimumRow = row + 1;
            }

            return rows;
        });
    }

    #minimizeCrossings(columns: DeviceClassColumn[], connections: Set<string>): void {
        const adjacentStepIds = new Map<string, string[]>();

        for (const connection of connections) {
            const [sourceId, targetId] = connection.split('\u0000');
            adjacentStepIds.set(sourceId, [...(adjacentStepIds.get(sourceId) ?? []), targetId]);
            adjacentStepIds.set(targetId, [...(adjacentStepIds.get(targetId) ?? []), sourceId]);
        }

        const rowByStepId = new Map<string, number>();

        for (const column of columns) {
            column.connected.sort((left, right) => {
                const leftRow = this.#averageNeighborRow(left.opId, adjacentStepIds, rowByStepId);
                const rightRow = this.#averageNeighborRow(right.opId, adjacentStepIds, rowByStepId);

                if (leftRow === null && rightRow === null) {
                    return this.#sortOrder(left) - this.#sortOrder(right);
                }
                if (leftRow === null) {
                    return 1;
                }
                if (rightRow === null) {
                    return -1;
                }
                return leftRow - rightRow || this.#sortOrder(left) - this.#sortOrder(right);
            });
            column.connected.forEach((step, rowIndex) => rowByStepId.set(step.opId, rowIndex));
        }
    }

    #averageNeighborRow(
        stepId: string,
        adjacentStepIds: Map<string, string[]>,
        rowByStepId: Map<string, number>
    ): number | null {
        const neighborRows = (adjacentStepIds.get(stepId) ?? []).flatMap((neighborId) => {
            const row = rowByStepId.get(neighborId);
            return row === undefined ? [] : [row];
        });

        return neighborRows.length
            ? neighborRows.reduce((total, neighborRow) => total + neighborRow, 0) / neighborRows.length
            : null;
    }

    #connections(workSteps: JobFlowWorkStep[]): Set<string> {
        const stepIds = new Set(workSteps.map((step) => step.opId));
        const connections = new Set<string>();

        for (const step of workSteps) {
            step.successors?.forEach(({ workstepId }) => {
                if (stepIds.has(workstepId)) {
                    connections.add(`${step.opId}\u0000${workstepId}`);
                }
            });
            step.predecessors?.forEach(({ workstepId }) => {
                if (stepIds.has(workstepId)) {
                    connections.add(`${workstepId}\u0000${step.opId}`);
                }
            });
        }

        return connections;
    }

    #createEdges(connections: DiagramConnection[], nodes: PositionedDiagramNode[]): PositionedEdge[] {
        const nodesById = new Map(nodes.map((node) => [node.id, node]));
        const routedConnections = connections.flatMap(({ id: connection, sourceId, targetId }): RoutedConnection[] => {
            const source = nodesById.get(sourceId);
            const target = nodesById.get(targetId);

            return source && target ? [{ connection, sourceId, targetId, source, target }] : [];
        });
        const endpoints: RoutingEndpoint[] = routedConnections.flatMap(
            ({ connection, sourceId, targetId, source, target }) => [
                { connection, role: 'source', nodeId: sourceId, node: source, otherNode: target },
                { connection, role: 'target', nodeId: targetId, node: target, otherNode: source },
            ]
        );
        const portGroups = this.#groupBy(
            endpoints,
            ({ nodeId, node, otherNode }) => `${nodeId}\u0000${this.#portSide(node, otherNode)}`
        );
        for (const portGroup of portGroups.values()) {
            portGroup.sort(
                (left, right) =>
                    left.otherNode.y + left.otherNode.height / 2 - (right.otherNode.y + right.otherNode.height / 2)
            );
        }
        const preparedConnections = routedConnections.map(
            ({ connection, sourceId, targetId, source, target }, index) => {
                const sourcePorts = portGroups.get(`${sourceId}\u0000${this.#portSide(source, target)}`) ?? [];
                const targetPorts = portGroups.get(`${targetId}\u0000${this.#portSide(target, source)}`) ?? [];

                return {
                    index,
                    source,
                    target,
                    sourcePort: this.#port(
                        source,
                        target,
                        sourcePorts.findIndex(
                            (endpoint) => endpoint.connection === connection && endpoint.role === 'source'
                        ),
                        sourcePorts.length
                    ),
                    targetPort: this.#port(
                        target,
                        source,
                        targetPorts.findIndex(
                            (endpoint) => endpoint.connection === connection && endpoint.role === 'target'
                        ),
                        targetPorts.length
                    ),
                };
            }
        );
        const usedSegments: RouteSegment[] = [];
        const edges = [...preparedConnections]
            .sort(
                (left, right) =>
                    Math.abs(right.target.x - right.source.x) - Math.abs(left.target.x - left.source.x) ||
                    left.index - right.index
            )
            .map(({ index, sourcePort, targetPort }) => {
                const routedPoints = this.#route(sourcePort.escape, targetPort.escape, nodes, usedSegments);
                const points = this.#simplifyPoints([
                    sourcePort.boundary,
                    sourcePort.escape,
                    ...routedPoints.slice(1, -1),
                    targetPort.escape,
                    targetPort.boundary,
                ]);
                usedSegments.push(...this.#segments(points));

                return { index, edge: { id: `edge-${index}`, points } };
            });

        return [...edges].sort((left, right) => left.index - right.index).map(({ edge }) => edge);
    }

    #port(
        node: PositionedDiagramNode,
        otherNode: PositionedDiagramNode,
        portIndex: number,
        portCount: number
    ): RoutingPort {
        const y = node.y + ((portIndex + 1) * node.height) / (portCount + 1);
        const useRightSide = this.#portSide(node, otherNode) === 'right';
        const x = useRightSide ? node.x + node.width : node.x;

        return {
            boundary: { x, y },
            escape: { x: x + (useRightSide ? EDGE_PORT_LENGTH : -EDGE_PORT_LENGTH), y },
        };
    }

    #portSide(node: PositionedDiagramNode, otherNode: PositionedDiagramNode): 'left' | 'right' {
        return node.x <= otherNode.x ? 'right' : 'left';
    }

    #route(
        start: DiagramPoint,
        target: DiagramPoint,
        nodes: PositionedDiagramNode[],
        usedSegments: RouteSegment[]
    ): DiagramPoint[] {
        const obstacles = nodes.map((node) => ({
            left: node.x - EDGE_CLEARANCE,
            right: node.x + node.width + EDGE_CLEARANCE,
            top: node.y - EDGE_CLEARANCE,
            bottom: node.y + node.height + EDGE_CLEARANCE,
        }));
        const grid = this.#routingGrid(start, target, nodes, usedSegments, obstacles);
        const startKey = this.#routeKey(grid.startXIndex, grid.startYIndex, 'start');
        const queue: RouteQueueItem[] = [
            {
                key: startKey,
                xIndex: grid.startXIndex,
                yIndex: grid.startYIndex,
                direction: 'start',
                cost: 0,
                estimatedCost: this.#distance(start, target),
            },
        ];
        const costs = new Map([[startKey, 0]]);
        const previous = new Map<string, string>();
        const states = new Map<string, RouteQueueItem>([[startKey, queue[0]]]);
        let targetKey: string | undefined;

        while (queue.length) {
            const current = this.#popQueue(queue);
            if (!current || current.cost !== costs.get(current.key)) {
                continue;
            }
            if (current.xIndex === grid.targetXIndex && current.yIndex === grid.targetYIndex) {
                targetKey = current.key;
                break;
            }

            this.#expandRouteNode(current, grid, obstacles, usedSegments, target, costs, previous, states, queue);
        }

        return targetKey
            ? this.#reconstructRoute(targetKey, states, previous, grid.xValues, grid.yValues)
            : this.#fallbackRoute(start, target, nodes);
    }

    #routingGrid(
        start: DiagramPoint,
        target: DiagramPoint,
        nodes: PositionedDiagramNode[],
        usedSegments: RouteSegment[],
        obstacles: RoutingObstacle[]
    ): {
        xValues: number[];
        yValues: number[];
        startXIndex: number;
        startYIndex: number;
        targetXIndex: number;
        targetYIndex: number;
    } {
        const xValues = this.#routingCoordinates(
            [
                start.x,
                target.x,
                Math.min(...nodes.map((node) => node.x)) - EDGE_CLEARANCE * 2,
                Math.max(...nodes.map((node) => node.x + node.width)) + DEFAULT_COLUMN_GAP - EDGE_CLEARANCE,
                ...obstacles.flatMap(({ left, right }) => [left, right]),
                ...usedSegments
                    .filter((segment) => segment.start.x === segment.end.x)
                    .flatMap((segment) => [segment.start.x - EDGE_CLEARANCE, segment.start.x + EDGE_CLEARANCE]),
            ],
            EDGE_CLEARANCE
        );
        const yValues = this.#routingCoordinates(
            [
                start.y,
                target.y,
                Math.min(...nodes.map((node) => node.y)) - EDGE_CLEARANCE * 2,
                Math.max(...nodes.map((node) => node.y + node.height)) + SCENE_PADDING,
                ...obstacles.flatMap(({ top, bottom }) => [top, bottom]),
                ...usedSegments
                    .filter((segment) => segment.start.y === segment.end.y)
                    .flatMap((segment) => [segment.start.y - EDGE_CLEARANCE, segment.start.y + EDGE_CLEARANCE]),
            ],
            EDGE_CLEARANCE
        );

        return {
            xValues,
            yValues,
            startXIndex: xValues.indexOf(start.x),
            startYIndex: yValues.indexOf(start.y),
            targetXIndex: xValues.indexOf(target.x),
            targetYIndex: yValues.indexOf(target.y),
        };
    }

    #expandRouteNode(
        current: RouteQueueItem,
        grid: {
            xValues: number[];
            yValues: number[];
            targetXIndex: number;
            targetYIndex: number;
        },
        obstacles: RoutingObstacle[],
        usedSegments: RouteSegment[],
        target: DiagramPoint,
        costs: Map<string, number>,
        previous: Map<string, string>,
        states: Map<string, RouteQueueItem>,
        queue: RouteQueueItem[]
    ): void {
        const neighbors = [
            { xIndex: current.xIndex - 1, yIndex: current.yIndex, direction: 'horizontal' as const },
            { xIndex: current.xIndex + 1, yIndex: current.yIndex, direction: 'horizontal' as const },
            { xIndex: current.xIndex, yIndex: current.yIndex - 1, direction: 'vertical' as const },
            { xIndex: current.xIndex, yIndex: current.yIndex + 1, direction: 'vertical' as const },
        ];

        for (const neighbor of neighbors) {
            if (
                neighbor.xIndex < 0 ||
                neighbor.yIndex < 0 ||
                neighbor.xIndex >= grid.xValues.length ||
                neighbor.yIndex >= grid.yValues.length
            ) {
                continue;
            }

            const from = { x: grid.xValues[current.xIndex], y: grid.yValues[current.yIndex] };
            const to = { x: grid.xValues[neighbor.xIndex], y: grid.yValues[neighbor.yIndex] };
            if (this.#insideObstacle(to, obstacles) || !this.#segmentClear({ start: from, end: to }, obstacles)) {
                continue;
            }

            const bendCost =
                current.direction !== 'start' && current.direction !== neighbor.direction ? EDGE_BEND_COST : 0;
            const cost =
                current.cost +
                this.#distance(from, to) +
                bendCost +
                this.#edgeConflictCost({ start: from, end: to }, usedSegments);
            const key = this.#routeKey(neighbor.xIndex, neighbor.yIndex, neighbor.direction);
            if (cost >= (costs.get(key) ?? Number.POSITIVE_INFINITY)) {
                continue;
            }

            const state: RouteQueueItem = {
                key,
                ...neighbor,
                cost,
                estimatedCost: cost + this.#distance(to, target),
            };
            costs.set(key, cost);
            previous.set(key, current.key);
            states.set(key, state);
            this.#pushQueue(queue, state);
        }
    }

    #reconstructRoute(
        targetKey: string,
        states: Map<string, RouteQueueItem>,
        previous: Map<string, string>,
        xValues: number[],
        yValues: number[]
    ): DiagramPoint[] {
        const points: DiagramPoint[] = [];
        for (let key: string | undefined = targetKey; key; key = previous.get(key)) {
            const state = states.get(key);
            if (state) {
                points.push({ x: xValues[state.xIndex], y: yValues[state.yIndex] });
            }
        }

        return points.reverse();
    }

    #fallbackRoute(start: DiagramPoint, target: DiagramPoint, nodes: PositionedDiagramNode[]): DiagramPoint[] {
        const fallbackY = Math.max(...nodes.map((node) => node.y + node.height)) + SCENE_PADDING;
        return [start, { x: start.x, y: fallbackY }, { x: target.x, y: fallbackY }, target];
    }

    #routingCoordinates(values: number[], minimumGap: number): number[] {
        const coordinates = [...new Set(values)].sort((left, right) => left - right);
        const midpoints = coordinates.slice(1).flatMap((value, index) => {
            const previous = coordinates[index];
            return value - previous > minimumGap * 2 ? [(previous + value) / 2] : [];
        });
        return [...new Set([...coordinates, ...midpoints])].sort((left, right) => left - right);
    }

    #insideObstacle(point: DiagramPoint, obstacles: RoutingObstacle[]): boolean {
        return obstacles.some(
            ({ left, right, top, bottom }) => point.x > left && point.x < right && point.y > top && point.y < bottom
        );
    }

    #segmentClear(segment: RouteSegment, obstacles: RoutingObstacle[]): boolean {
        return obstacles.every(({ left, right, top, bottom }) => {
            if (segment.start.y === segment.end.y) {
                const minX = Math.min(segment.start.x, segment.end.x);
                const maxX = Math.max(segment.start.x, segment.end.x);
                return segment.start.y <= top || segment.start.y >= bottom || maxX <= left || minX >= right;
            }

            const minY = Math.min(segment.start.y, segment.end.y);
            const maxY = Math.max(segment.start.y, segment.end.y);
            return segment.start.x <= left || segment.start.x >= right || maxY <= top || minY >= bottom;
        });
    }

    #edgeConflictCost(segment: RouteSegment, usedSegments: RouteSegment[]): number {
        return usedSegments.reduce((cost, usedSegment) => {
            const segmentHorizontal = segment.start.y === segment.end.y;
            const usedHorizontal = usedSegment.start.y === usedSegment.end.y;

            if (segmentHorizontal === usedHorizontal) {
                const sameLine = segmentHorizontal
                    ? segment.start.y === usedSegment.start.y
                    : segment.start.x === usedSegment.start.x;
                if (!sameLine) {
                    return cost;
                }

                const overlap = segmentHorizontal
                    ? this.#rangeOverlap(segment.start.x, segment.end.x, usedSegment.start.x, usedSegment.end.x)
                    : this.#rangeOverlap(segment.start.y, segment.end.y, usedSegment.start.y, usedSegment.end.y);
                return cost + (overlap > 0 ? EDGE_OVERLAP_COST + overlap : 0);
            }

            const horizontal = segmentHorizontal ? segment : usedSegment;
            const vertical = segmentHorizontal ? usedSegment : segment;
            const crosses =
                vertical.start.x > Math.min(horizontal.start.x, horizontal.end.x) &&
                vertical.start.x < Math.max(horizontal.start.x, horizontal.end.x) &&
                horizontal.start.y > Math.min(vertical.start.y, vertical.end.y) &&
                horizontal.start.y < Math.max(vertical.start.y, vertical.end.y);
            return cost + (crosses ? EDGE_CROSSING_COST : 0);
        }, 0);
    }

    #rangeOverlap(start1: number, end1: number, start2: number, end2: number): number {
        return Math.max(
            0,
            Math.min(Math.max(start1, end1), Math.max(start2, end2)) -
                Math.max(Math.min(start1, end1), Math.min(start2, end2))
        );
    }

    #segments(points: DiagramPoint[]): RouteSegment[] {
        return points.slice(1).map((point, index) => ({ start: points[index], end: point }));
    }

    #simplifyPoints(points: DiagramPoint[]): DiagramPoint[] {
        const uniquePoints = points.filter(
            (point, index) => !index || point.x !== points[index - 1].x || point.y !== points[index - 1].y
        );

        return uniquePoints.filter((point, index) => {
            if (!index || index === uniquePoints.length - 1) {
                return true;
            }
            const previous = uniquePoints[index - 1];
            const next = uniquePoints[index + 1];
            return !(previous.x === point.x && point.x === next.x) && !(previous.y === point.y && point.y === next.y);
        });
    }

    #distance(point1: DiagramPoint, point2: DiagramPoint): number {
        return Math.abs(point1.x - point2.x) + Math.abs(point1.y - point2.y);
    }

    #routeKey(xIndex: number, yIndex: number, direction: RouteDirection): string {
        return `${xIndex}:${yIndex}:${direction}`;
    }

    #pushQueue(queue: RouteQueueItem[], item: RouteQueueItem): void {
        queue.push(item);
        let index = queue.length - 1;

        while (index > 0) {
            const parentIndex = Math.floor((index - 1) / 2);
            if (queue[parentIndex].estimatedCost <= item.estimatedCost) {
                break;
            }
            queue[index] = queue[parentIndex];
            index = parentIndex;
        }
        queue[index] = item;
    }

    #popQueue(queue: RouteQueueItem[]): RouteQueueItem | undefined {
        const first = queue[0];
        const last = queue.pop();

        if (queue.length && last) {
            let index = 0;
            let leftIndex = index * 2 + 1;
            while (leftIndex < queue.length) {
                const rightIndex = leftIndex + 1;
                const childIndex =
                    rightIndex < queue.length && queue[rightIndex].estimatedCost < queue[leftIndex].estimatedCost
                        ? rightIndex
                        : leftIndex;
                if (queue[childIndex].estimatedCost >= last.estimatedCost) {
                    break;
                }
                queue[index] = queue[childIndex];
                index = childIndex;
                leftIndex = index * 2 + 1;
            }
            queue[index] = last;
        }

        return first;
    }

    #groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
        const groups = new Map<string, T[]>();

        for (const item of items) {
            const itemKey = key(item);
            groups.set(itemKey, [...(groups.get(itemKey) ?? []), item]);
        }

        return groups;
    }

    #sortOrder(step: JobFlowWorkStep): number {
        return Number.isFinite(step.sortOrder) ? step.sortOrder : Number.MAX_SAFE_INTEGER;
    }

    #rowHeight(step: JobFlowWorkStep, excludeStacks = false): number {
        return Math.max(NODE_HEIGHT, excludeStacks ? 0 : this.#stackGroupHeight(step.stacks));
    }

    #stackGroupHeight(stacks: JobFlowStack[], hasLabel = false): number {
        if (!stacks.length) {
            return 0;
        }

        const labelHeight = hasLabel ? STACK_GROUP_LABEL_HEIGHT + STACK_NODE_GAP : 0;
        return (
            STACK_GROUP_PADDING * 2 +
            stacks.length * STACK_NODE_HEIGHT +
            (stacks.length - 1) * STACK_NODE_GAP +
            labelHeight
        );
    }
}
